import { IssueLiabilityDoc, IssueLiabilityStatus, issueLiabilityStatusMap } from '@gooduncles/gu-app-schema';
import { Card, Col, DatePicker, Row, Spin, Statistic } from 'antd';
import dayjs from 'dayjs';
import { FC, useEffect, useMemo, useState } from 'react';

import { map } from 'rxjs';

import { observeIssueLiabilities } from 'src/utils/firebase/firebase-short-cut';

const statusColorMap: Record<IssueLiabilityStatus, string> = {
  registered: '#1890ff',
  delivered: '#faad14',
  settled: '#52c41a',
  unsettled: '#ff4d4f',
  closed: '#8c8c8c',
};

interface IssueLiabilityDashboardProps {
  selectedMonth: dayjs.Dayjs;
  onMonthChange: (month: dayjs.Dayjs) => void;
}

/**
 * 월별 손실 책임 대시보드
 * - 월 선택 기능
 * - 총 손실액, 총 건수, 상태별 건수(등록, 전달 완료, 정산 완료, 미정산, 종결) 표시
 */
const IssueLiabilityDashboard: FC<IssueLiabilityDashboardProps> = ({ selectedMonth, onMonthChange }) => {
  const [liabilities, setLiabilities] = useState<IssueLiabilityDoc[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // 월의 시작일/종료일 계산
  const [startDate, endDate] = useMemo(() => {
    const start = selectedMonth.startOf('month').format('YYYY-MM-DD');
    const end = selectedMonth.endOf('month').format('YYYY-MM-DD');
    return [start, end];
  }, [selectedMonth]);

  // 월별 데이터 구독
  useEffect(() => {
    setIsLoading(true);

    const subscription = observeIssueLiabilities(
      [
        ['registeredAt', '>=', startDate],
        ['registeredAt', '<=', endDate],
      ],
      {
        sortKey: 'registeredAt',
        orderBy: 'desc',
      },
    )
      .pipe(map((docs) => docs.filter((doc) => doc.deletedAt == null)))
      .subscribe({
        next: (docs) => {
          setLiabilities(docs);
          setIsLoading(false);
        },
        error: (error) => {
          console.error('Failed to fetch liabilities for dashboard:', error);
          setIsLoading(false);
        },
      });

    return () => subscription.unsubscribe();
  }, [startDate, endDate]);

  // 통계 계산
  const stats = useMemo(() => {
    const totalAmount = liabilities.reduce((sum, item) => sum + item.amount.totalAmount, 0);
    const totalCount = liabilities.length;

    // 상태별 건수 및 금액 계산
    const statusStats = liabilities.reduce(
      (acc, item) => {
        if (!acc[item.status]) {
          acc[item.status] = { count: 0, amount: 0 };
        }
        acc[item.status].count += 1;
        acc[item.status].amount += item.amount.totalAmount;
        return acc;
      },
      {} as Record<string, { count: number; amount: number }>,
    );

    const getStatusStats = (status: string) => statusStats[status] || { count: 0, amount: 0 };

    return {
      totalAmount,
      totalCount,
      registered: getStatusStats('registered'),
      delivered: getStatusStats('delivered'),
      settled: getStatusStats('settled'),
      unsettled: getStatusStats('unsettled'),
      closed: getStatusStats('closed'),
    };
  }, [liabilities]);

  const handleMonthChange = (date: dayjs.Dayjs | null) => {
    if (date) {
      onMonthChange(date);
    }
  };

  return (
    <Card size='small' title='월별 손실 책임 요약'>
      <Spin spinning={isLoading}>
        <Row gutter={24} align='middle'>
          <Col span={3}>
            <DatePicker picker='month' value={selectedMonth} onChange={handleMonthChange} allowClear={false} />
          </Col>
          <Col span={3}>
            <Statistic title='합계' value={stats.totalCount} suffix='건' />
            <Statistic value={stats.totalAmount} prefix='₩' valueStyle={{ fontSize: 16 }} />
          </Col>
          {(['registered', 'delivered', 'settled', 'unsettled', 'closed'] as IssueLiabilityStatus[]).map((status) => (
            <Col key={status} span={3}>
              <Statistic
                title={issueLiabilityStatusMap[status]}
                value={stats[status].count}
                suffix='건'
                valueStyle={{ color: statusColorMap[status] }}
              />
              <Statistic value={stats[status].amount} prefix='₩' valueStyle={{ fontSize: 16 }} />
            </Col>
          ))}
        </Row>
      </Spin>
    </Card>
  );
};

export default IssueLiabilityDashboard;
